// Recepción — reconcile / discrepancy review (/po/scan/:id/revision).
// Expected-vs-scanned table (short/over/unexpected filters, by-device
// expansion), a summary strip, a discrepancy PDF export, and the gated
// "Postear recepción" panel that receives the scanned lines into the ERP
// via paceWriteServ's post-receiving verb (recvpost.p, Task 13).
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  Badge, Button, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import { getReception, postReception, reconcileReception, WritesDisabledError } from '../api/reception';
import AccessDenied from '../components/AccessDenied';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import Header from '../components/Header';
import TableSkeleton from '../components/TableSkeleton';
import { exportReportPdf } from '../lib/pdf';
import {
  postReceivingStatusColor, postReceivingStatusLabel,
  receptionStatusColor, receptionStatusLabel, reconClassColor, reconClassLabel,
} from '../lib/format';
import { canPostReceiving, canUseReceiving } from '../lib/roles';
import { t } from '../lib/i18n';
import type { PostResult, ReceptionSession, ReconLine, ReconReport, User } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });

function fmtVariance(v: number): string {
  return v > 0 ? `+${v}` : String(v);
}

export default function ReceptionReview() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        {canUseReceiving(user) ? <ReceptionReviewInner user={user} /> : <AccessDenied user={user} />}
      </main>
    </div>
  );
}

type Filter = 'all' | 'short' | 'over' | 'unexpected';
const FILTERS: Filter[] = ['all', 'short', 'over', 'unexpected'];

function ReceptionReviewInner({ user }: { user: User }) {
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id);

  const [session, setSession] = useState<ReceptionSession | null>(null);
  const [report, setReport] = useState<ReconReport | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = () => {
    if (!Number.isFinite(sessionId)) { setLoadErr(t.recepcion.errors.generic); return; }
    setLoadErr(null);
    setSession(null);
    setReport(null);
    Promise.all([getReception(sessionId), reconcileReception(sessionId)])
      .then(([s, r]) => { setSession(s); setReport(r); })
      .catch((e: Error) => setLoadErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sessionId]);

  // Post-panel refresh: re-fetch session + reconcile IN PLACE (unlike load(),
  // does not null session/report first) so a post doesn't flash the page back
  // to the skeleton and unmount PostPanel's own results/error state.
  function refresh(): Promise<void> {
    return Promise.all([getReception(sessionId), reconcileReception(sessionId)])
      .then(([s, r]) => { setSession(s); setReport(r); })
      .catch((e: Error) => setLoadErr(e.message ?? String(e)));
  }

  const showValue = report != null && report.lines.some((l) => l.value != null);

  const filteredLines = useMemo(() => {
    if (!report) return [];
    switch (filter) {
      case 'short':      return report.lines.filter((l) => l.class === 'short');
      case 'over':       return report.lines.filter((l) => l.class === 'over');
      case 'unexpected': return [];
      default:           return report.lines;
    }
  }, [report, filter]);

  function toggleExpand(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function handleExportPdf() {
    if (!report || !session) return;
    const columns: string[] = [
      t.recepcion.review.cols.part, t.recepcion.review.cols.desc,
      t.recepcion.review.cols.expected, t.recepcion.review.cols.scanned, t.recepcion.review.cols.diff,
    ];
    const numericColumns = [2, 3, 4];
    if (showValue) { columns.push(t.recepcion.review.cols.value); numericColumns.push(5); }
    columns.push(t.recepcion.review.cols.class);

    const rows: Array<Array<string | number>> = report.lines.map((l) => {
      const row: Array<string | number> = [l.part, l.pdesc, l.expected, l.scanned, fmtVariance(l.variance)];
      if (showValue) row.push(l.value != null ? fmtMxn.format(l.value) : '—');
      row.push(reconClassLabel(l.class));
      return row;
    });

    exportReportPdf({
      title: t.recepcion.review.printTitle,
      meta: [`OC #${session.onum} · ${session.sup_name || session.sup_id || '—'} · Tienda #${session.sid}`],
      printedBy: user.username,
      columns,
      rows,
      numericColumns,
      filename: `recepcion_${session.id}_conciliacion.pdf`,
    });
  }

  if (loadErr) {
    return (
      <div className="space-y-4">
        <BackLink id={id} />
        <ErrorRetry message={loadErr} onRetry={load} />
      </div>
    );
  }
  if (session === null || report === null) {
    return (
      <div className="space-y-4">
        <BackLink id={id} />
        <TableSkeleton rows={8} cols={6} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <BackLink id={id} />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Title>{t.recepcion.review.title} — OC #{session.onum}</Title>
            <Badge color={receptionStatusColor(session.status)}>{receptionStatusLabel(session.status)}</Badge>
          </div>
          <Text className="text-tremor-content text-sm">
            {session.sup_name || session.sup_id || '—'} · Tienda #{session.sid}
          </Text>
        </div>
      </div>

      <Card className={`grid grid-cols-2 md:grid-cols-3 ${showValue ? 'lg:grid-cols-6' : 'lg:grid-cols-5'} gap-4`}>
        <Kpi label={t.recepcion.review.kpi.lines} value={String(report.summary.lines)} />
        <Kpi label={t.recepcion.review.kpi.short} value={String(report.summary.short)} />
        <Kpi label={t.recepcion.review.kpi.over} value={String(report.summary.over)} />
        <Kpi label={t.recepcion.review.kpi.unexpected} value={String(report.summary.unexpected)} />
        <Kpi label={t.recepcion.review.kpi.netPacks} value={fmtVariance(report.summary.net_packs)} />
        {showValue && (
          <Kpi label={t.recepcion.review.kpi.netValue} value={fmtMxn.format(report.summary.net_value ?? 0)} />
        )}
      </Card>

      <Card className="flex flex-wrap items-end gap-3">
        <div className="flex gap-1 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={
                'px-2 py-1 rounded border text-xs ' +
                (filter === f
                  ? 'bg-tremor-brand text-tremor-brand-inverted border-tremor-brand'
                  : 'border-tremor-border text-tremor-content hover:text-tremor-content-strong')
              }
            >
              {t.recepcion.review.filters[f]}
            </button>
          ))}
        </div>
        <Button size="xs" variant="secondary" className="ml-auto" onClick={handleExportPdf} disabled={report.lines.length === 0}>
          {t.recepcion.review.exportPdf}
        </Button>
      </Card>

      {filter !== 'unexpected' && (
        filteredLines.length === 0 ? (
          <EmptyState message={t.recepcion.review.empty} />
        ) : (
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t.recepcion.review.cols.part}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.recepcion.review.cols.expected}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.recepcion.review.cols.scanned}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.recepcion.review.cols.diff}</TableHeaderCell>
                    {showValue && <TableHeaderCell className="text-right">{t.recepcion.review.cols.value}</TableHeaderCell>}
                    <TableHeaderCell>{t.recepcion.review.cols.class}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.recepcion.review.byDevice}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLines.map((l) => (
                    <ReconLineRows
                      key={`${l.line}-${l.part}`}
                      line={l}
                      showValue={showValue}
                      expanded={expanded.has(`${l.line}-${l.part}`)}
                      onToggle={() => toggleExpand(`${l.line}-${l.part}`)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )
      )}

      {(filter === 'all' || filter === 'unexpected') && (
        <Card className="p-0 overflow-hidden">
          <Title className="text-base px-4 pt-4">{t.recepcion.review.unexpectedTitle}</Title>
          {report.unexpected.length === 0 ? (
            <div className="p-4">
              <Text className="text-sm text-tremor-content">{t.recepcion.review.unexpectedEmpty}</Text>
            </div>
          ) : (
            <div className="overflow-x-auto mt-2">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t.recepcion.review.cols.part}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.recepcion.review.cols.scanned}</TableHeaderCell>
                    <TableHeaderCell>{t.recepcion.review.cols.class}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.recepcion.review.byDevice}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.unexpected.map((u) => {
                    const key = `u-${u.line}-${u.part}`;
                    const deviceEntries = Object.entries(u.by_device).sort((a, b) => b[1] - a[1]);
                    return (
                      <TableRow key={key} className="cursor-pointer hover:bg-tremor-background-muted" onClick={() => toggleExpand(key)}>
                        <TableCell className="font-medium">{u.part}</TableCell>
                        <TableCell className="text-right tabular-nums">{u.scanned}</TableCell>
                        <TableCell><Badge color={reconClassColor('unexpected')}>{reconClassLabel('unexpected')}</Badge></TableCell>
                        <TableCell className="text-right text-xs text-tremor-content">
                          {expanded.has(key)
                            ? deviceEntries.map(([d, q]) => `${d}: ${q}`).join(' · ')
                            : `${deviceEntries.length}`}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      )}

      <PostPanel user={user} session={session} onSessionUpdate={setSession} refresh={refresh} />
    </div>
  );
}

interface ReconLineRowsProps {
  line: ReconLine;
  showValue: boolean;
  expanded: boolean;
  onToggle: () => void;
}

function ReconLineRows({ line, showValue, expanded, onToggle }: ReconLineRowsProps) {
  const deviceEntries = Object.entries(line.by_device).sort((a, b) => b[1] - a[1]);
  return (
    <>
      <TableRow className="cursor-pointer hover:bg-tremor-background-muted" onClick={onToggle}>
        <TableCell>
          <div className="font-medium">{line.part}</div>
          <div className="text-xs text-tremor-content">{line.pdesc}</div>
        </TableCell>
        <TableCell className="text-right tabular-nums">{line.expected}</TableCell>
        <TableCell className="text-right tabular-nums">{line.scanned}</TableCell>
        <TableCell
          className={
            'text-right tabular-nums font-medium ' +
            (line.variance > 0
              ? 'text-emerald-600 dark:text-emerald-400'
              : line.variance < 0
                ? 'text-red-600 dark:text-red-400'
                : '')
          }
        >
          {fmtVariance(line.variance)}
        </TableCell>
        {showValue && (
          <TableCell className="text-right tabular-nums">
            {line.value != null ? fmtMxn.format(line.value) : '—'}
          </TableCell>
        )}
        <TableCell><Badge color={reconClassColor(line.class)}>{reconClassLabel(line.class)}</Badge></TableCell>
        <TableCell className="text-right text-xs text-tremor-content">
          {expanded ? '▾' : '▸'} {deviceEntries.length}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={showValue ? 7 : 6} className="bg-tremor-background-muted">
            {deviceEntries.length === 0 ? (
              <Text className="text-xs text-tremor-content">—</Text>
            ) : (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs py-1">
                {deviceEntries.map(([d, q]) => (
                  <span key={d}><span className="font-medium">{d}</span>: {q}</span>
                ))}
              </div>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Postear recepción — gated ERP receive (clones InventoryCountSession.tsx's
// Postear panel: same permission/writes-enabled/terminal-status gating and
// the same "attempted → relabel to restantes" button behavior).
// ---------------------------------------------------------------------------

interface PostPanelProps {
  user: User;
  session: ReceptionSession;
  onSessionUpdate: (session: ReceptionSession) => void;
  refresh: () => Promise<void>;
}

function PostPanel({ user, session, onSessionUpdate, refresh }: PostPanelProps) {
  const canPost = canPostReceiving(user);
  const writesEnabled = !!user.features?.writes_enabled;

  const [posting, setPosting] = useState(false);
  const [postErr, setPostErr] = useState<string | null>(null);
  const [results, setResults] = useState<PostResult[] | null>(null);

  if (!canPost) {
    return (
      <Card>
        <Title className="text-base">{t.recepcion.review.post.title}</Title>
        <Text className="text-tremor-content text-sm mt-2">{t.recepcion.review.post.noPermission}</Text>
      </Card>
    );
  }

  if (!writesEnabled) {
    return (
      <Card>
        <Text className="text-amber-600 dark:text-amber-400">{t.recepcion.review.post.writesOff}</Text>
      </Card>
    );
  }

  const recibido = session.status === 'recibido';
  // Reload-safe "has this session already had a successful post" signal:
  // receive_num is only ever set once recvpost.p assigns an ERP receive
  // event (handlers_reception.go's SetSessionReceiveNum), so — unlike a
  // local-only boolean — it still reads correctly after a reload mid-way
  // through a partially-received session. Mirrors physcount's
  // `lines.some(l => l.post_status !== '')`, adapted: ReconLine (this page's
  // line shape) carries no post_status field, so the durable signal here is
  // session-level instead of line-level.
  const alreadyAttempted = session.receive_num != null;

  async function handlePost() {
    setPosting(true);
    setPostErr(null);
    try {
      const res = await postReception(session.id);
      setResults(res.results);
      onSessionUpdate(res.session);
      await refresh();
    } catch (e) {
      setPostErr(e instanceof WritesDisabledError ? t.recepcion.review.post.writesOff : (e as Error).message ?? t.recepcion.errors.generic);
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card className="space-y-4">
        <Title className="text-base">{t.recepcion.review.post.title}</Title>

        {recibido ? (
          <div>
            <Text className="text-emerald-600 dark:text-emerald-400 font-medium">{t.recepcion.review.post.recibido}</Text>
            {session.posted_by && (
              <Text className="text-tremor-content text-xs mt-1">
                {session.posted_by} · {session.posted_at ? new Date(session.posted_at).toLocaleString('es-MX') : ''}
              </Text>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <Button onClick={handlePost} loading={posting} disabled={posting}>
                {alreadyAttempted ? t.recepcion.review.post.restantes : t.recepcion.review.post.button}
              </Button>
            </div>
            {postErr && <Text className="text-red-600 dark:text-red-400 text-sm">{postErr}</Text>}
          </>
        )}
      </Card>

      {results !== null && results.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.recepcion.review.post.cols.part}</TableHeaderCell>
                  <TableHeaderCell>{t.recepcion.review.post.cols.status}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.recepcion.review.post.cols.received}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((r, i) => (
                  <TableRow key={`${r.line}-${r.part}-${i}`}>
                    <TableCell className="font-medium">{r.line} {r.part}</TableCell>
                    <TableCell>
                      <Badge color={postReceivingStatusColor(r.status)}>{postReceivingStatusLabel(r.status)}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.received}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function BackLink({ id }: { id?: string }) {
  return (
    <Link to={id ? `/po/scan/${id}` : '/po/scan'} className="text-sm text-tremor-brand hover:underline">
      {t.recepcion.review.back}
    </Link>
  );
}
